import React, { useState, useEffect, useCallback } from 'react';
import { Users, Flag, Ticket, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { VscLayersActive } from "react-icons/vsc";
import axios from 'axios';

const API_URL = window.location.origin.includes("192.168")
  ? "http://192.168.1.101:5010/api"
  : "http://localhost:5010/api";

const barData = [
  { name: 'Mon', races: 12 }, { name: 'Tue', races: 19 },
  { name: 'Wed', races: 8 }, { name: 'Thu', races: 24 },
  { name: 'Fri', races: 31 }, { name: 'Sat', races: 45 },
  { name: 'Sun', races: 38 },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TABLE_LIMIT = 10;

const customTooltipStyle = {
  background: '#16161f', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8, color: '#f0f0f5', fontSize: 12,
};

function formatBirthday(dobString) {
  if (!dobString) return '—';
  const d = new Date(dobString);
  if (isNaN(d)) return dobString;

  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();

  return `${day} ${month}, ${year}`;
}

function mapDriver(d) {
  return {
    id: d.serialNumber || d.id,
    _mongoId: d.id,
    name: d.name || d.callsign || 'Unknown',
    callsign: d.callsign || '—',
    email: d.email || '—',
    birthday: d.birthday || '',
    status: d.emailVerified
      ? (d.status === 'suspended' ? 'Blocked' : 'Active')
      : 'Unverified',
    joined: d.registeredAt
      ? new Date(d.registeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '—',
  };
}

function buildMonthlyData(drivers) {
  const now = new Date();
  const counts = {};
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    counts[key] = { month: MONTH_NAMES[d.getMonth()], users: 0 };
  }
  drivers.forEach(d => {
    if (!d.registeredAt) return;
    const date = new Date(d.registeredAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (counts[key]) counts[key].users += 1;
  });
  return Object.values(counts);
}

function Dashboard() {
  const [stats, setStats] = useState({ total: 0, active: 0, unverified: 0, newsletter: 0 });
  const [chartData, setChartData] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const [tableDrivers, setTableDrivers] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableCounts, setTableCounts] = useState({ active: 0, unverified: 0, blocked: 0 });
  const [tableLoading, setTableLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(1);

  // ── Stats + chart ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const [statsRes, chartRes, newsRes] = await Promise.all([
          axios.get(`${API_URL}/`, { params: { page: 1, limit: 1 } }),
          axios.get(`${API_URL}/`, { params: { page: 1, limit: 100 } }),
          axios.get(`${API_URL}/newsletter/subscribers`, { params: { page: 1, limit: 1 } }),
        ]);
        if (statsRes.data.status) {
          const { total, counts } = statsRes.data;
          setStats({
            total: total || 0,
            active: counts?.active || 0,
            unverified: counts?.unverified || 0,
            newsletter: newsRes.data?.total || 0,
          });
        }
        if (chartRes.data.status) {
          setChartData(buildMonthlyData(chartRes.data.drivers));
        }
      } catch (err) {
        console.error('[Dashboard] stats error:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  // ── Table ──────────────────────────────────────────────────────────────────
  const fetchTable = useCallback(async (pg, activeFilter) => {
    setTableLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/`, {
        params: { page: pg, limit: TABLE_LIMIT },
      });
      if (data.status) {
        let drivers = data.drivers.map(mapDriver);
        if (activeFilter !== 'All') {
          drivers = drivers.filter(d => d.status === activeFilter);
        }
        setTableDrivers(drivers);
        setTableTotal(data.total);
        setTableCounts(data.counts || { active: 0, unverified: 0, blocked: 0 });
      }
    } catch (err) {
      console.error('[Dashboard] table error:', err);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => { fetchTable(page, filter); }, [page, filter, fetchTable]);

  const handleFilterChange = (f) => { setFilter(f); setPage(1); };

  const totalPages = Math.ceil(tableTotal / TABLE_LIMIT);

  const filterCounts = {
    All: tableTotal,
    Active: tableCounts.active,
    Unverified: tableCounts.unverified,
    Blocked: tableCounts.blocked,
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back, Admin! Here's what's happening at Vegas Karts.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon red"><Users size={22} /></div>
          <div className="stat-info">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{statsLoading ? '—' : stats.total.toLocaleString()}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><VscLayersActive size={22} /></div>
          <div className="stat-info">
            <div className="stat-label">Active Users</div>
            <div className="stat-value">{statsLoading ? '—' : stats.active.toLocaleString()}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><Flag size={22} /></div>
          <div className="stat-info">
            <div className="stat-label">Total Tours</div>
            <div className="stat-value">4</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow"><Ticket size={22} /></div>
          <div className="stat-info">
            <div className="stat-label">Total Subscription</div>
            <div className="stat-value">2</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid-1" style={{ marginBottom: 24 }}>
        <div className="card">
          <div style={{ marginBottom: 20 }}>
            <h3 className="font-heading" style={{ fontSize: 16, color: 'var(--text-primary)' }}>Users Registrations</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Monthly registrations — last 7 months</p>
          </div>
          {!statsLoading && chartData.length === 0 ? (
            <div style={{
              height: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-muted)",
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 13,
              letterSpacing: 1
            }}>
              No Data Found
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={statsLoading ? [] : chartData}>
                <defs>
                  <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#e8001d" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#e8001d" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" stroke="#5a5a70" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#5a5a70" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={customTooltipStyle} />
                <Area type="monotone" dataKey="users" stroke="#e8001d" fill="url(#userGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card race_card">
          <div style={{ marginBottom: 20 }}>
            <h3 className="font-heading" style={{ fontSize: 16, color: 'var(--text-primary)' }}>Races Per Day</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>This week's racing activity</p>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="name" stroke="#5a5a70" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#5a5a70" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={customTooltipStyle} />
              <Bar dataKey="races" fill="#ff6b00" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Drivers Table */}
      <div className="card">
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 20,
        }}>
          <div>
            <h3 className="font-heading" style={{ fontSize: 16, color: 'var(--text-primary)' }}>Recent Users</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Latest registrations on Vegas Karts</p>
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['All', 'Active', 'Unverified', 'Blocked'].map(f => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8, border: '1px solid',
                  borderColor: filter === f ? 'var(--accent-red)' : 'var(--border)',
                  background: filter === f ? 'rgba(232,0,29,0.12)' : 'transparent',
                  color: filter === f ? 'var(--text-primary)' : 'var(--text-muted)',
                  cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 13,
                  transition: 'all 0.2s',
                }}
              >
                {f}
                <span style={{
                  background: filter === f ? 'rgba(232,0,29,0.25)' : 'rgba(255,255,255,0.06)',
                  color: filter === f ? '#ff6b6b' : 'var(--text-muted)',
                  borderRadius: 10, padding: '1px 7px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                }}>
                  {filterCounts[f] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Serial No.</th>
                <th>Name</th>
                <th>Callsign</th>
                <th>Email</th>
                <th>Birth Date</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: 2, fontSize: 13 }}>LOADING...</span>
                  </td>
                </tr>
              ) : tableDrivers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                    No drivers found.
                  </td>
                </tr>
              ) : (
                tableDrivers.map(u => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--accent-red)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                      {u.id}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: 'var(--accent-gradient)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 11, color: '#fff', flexShrink: 0,
                        }}>
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'var(--text-muted)' }}>
                      {u.callsign}
                    </td>
                    <td style={{ color: 'var(--text-muted)', minWidth: 160, wordBreak: 'break-all' }}>
                      {u.email}
                    </td>

                    {/* Birthday column */}
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {formatBirthday(u.birthday)}
                    </td>

                    <td>
                      <span className={`badge ${u.status === 'Active' ? 'badge-success' :
                          u.status === 'Blocked' ? 'badge-warning' : 'badge-danger'
                        }`}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{formatBirthday(u.joined)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!tableLoading && totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 20, flexWrap: 'wrap', gap: 12,
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Page {page} of {totalPages} · {tableTotal} total drivers
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)',
                  background: 'transparent', color: page === 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 14, opacity: page === 1 ? 0.4 : 1,
                }}>‹</button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>…</span>
                  ) : (
                    <button key={p} onClick={() => setPage(p)} style={{
                      width: 32, height: 32, borderRadius: 6, border: '1px solid',
                      borderColor: p === page ? 'var(--accent-red)' : 'var(--border)',
                      background: p === page ? 'rgba(232,0,29,0.12)' : 'transparent',
                      color: p === page ? 'var(--text-primary)' : 'var(--text-muted)',
                      cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13,
                    }}>{p}</button>
                  )
                )
              }

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)',
                  background: 'transparent', color: page === totalPages ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 14, opacity: page === totalPages ? 0.4 : 1,
                }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;